import { useCallback, FC, useEffect, useState, PropsWithChildren } from 'react';
import { useAppDispatch } from 'redux/hooks';
import styled, { keyframes } from 'styled-components';
import { signOut } from 'thunk';
import { AUTH_FAILURE_REDIRECT_DELAY_MS } from '@constants';
import { canAutoRedirectToLogin, recordAutoLoginRedirect } from 'utils/loginRedirectLoopGuard';
import { useAuthenticationLayer } from './useAuthenticationLayer';
import { Button, Box, Typography } from '@mui/material';
// @ts-expect-error - Vite SVG import typing issue
import { ReactComponent as Logo } from '../assets/mosaic_icon.svg';
import { StyledProps } from 'types/styled';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const LoadingContainer = styled(Box)<StyledProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: ${(props) =>
    props.theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, #0a0d1a 0%, #151b2e 100%)'
      : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'};
  padding: ${(props) => props.theme.spacing(4)}px;
`;

const LoadingCard = styled(Box)<StyledProps>`
  background: ${(props) =>
    props.theme.palette.mode === 'dark' ? 'rgba(20, 25, 40, 0.6)' : 'rgba(255, 255, 255, 0.6)'};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid
    ${(props) =>
      props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'};
  border-radius: 16px;
  padding: ${(props) => props.theme.spacing(8)}px ${(props) => props.theme.spacing(6)}px;
  max-width: 420px;
  width: 100%;
  text-align: center;
  animation: ${fadeIn} 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${(props) =>
    props.theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)'
      : '0 8px 32px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.5)'};
`;

const ErrorCard = styled(LoadingCard)<StyledProps>`
  background: ${(props) =>
    props.theme.palette.mode === 'dark' ? 'rgba(40, 20, 25, 0.6)' : 'rgba(255, 245, 245, 0.6)'};
  border-color: ${(props) =>
    props.theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.15)'};
  box-shadow: ${(props) =>
    props.theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(239, 68, 68, 0.15), 0 0 0 1px rgba(239, 68, 68, 0.1)'
      : '0 8px 32px rgba(239, 68, 68, 0.12), 0 0 0 1px rgba(239, 68, 68, 0.1)'};
`;

const LogoWrapper = styled.div<StyledProps>`
  display: flex;
  justify-content: center;
  margin-bottom: ${(props) => props.theme.spacing(4)}px;
  animation: ${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`;

const StyledLogo = styled(Logo)<StyledProps>`
  width: 48px;
  height: 48px;
  color: #6366f1;
  fill: #6366f1;
`;

const ProgressBarContainer = styled.div<StyledProps>`
  width: 100%;
  height: 6px;
  background: ${(props) =>
    props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
  border-radius: 3px;
  overflow: hidden;
  margin: ${(props) => props.theme.spacing(4)}px 0 ${(props) => props.theme.spacing(3)}px;
  position: relative;
`;

const ProgressBar = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #6366f1 100%);
  border-radius: 3px;
  position: relative;
  overflow: hidden;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.3) 50%,
      transparent 100%
    );
    animation: ${shimmer} 1.5s infinite;
  }
`;

const StyledButton = styled(Button)<StyledProps>`
  text-transform: none;
  font-weight: 500;
  border-radius: ${(props) => props.theme.spacing(2)}px; // 8px
  padding: ${(props) => props.theme.spacing(3)}px ${(props) => props.theme.spacing(6)}px; // 12px 24px
  font-size: 0.9375rem;
  margin-top: ${(props) => props.theme.spacing(2)}px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &.MuiButton-outlined {
    border-color: ${(props) => (props.theme?.palette?.mode === 'dark' ? '#333' : '#e5e5e5')};

    &:hover {
      border-color: ${(props) => (props.theme?.palette?.mode === 'dark' ? '#525252' : '#a3a3a3')};
      background-color: ${(props) =>
        props.theme?.palette?.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.04)'
          : 'rgba(0, 0, 0, 0.04)'};
    }
  }

  &.MuiButton-contained {
    box-shadow: none;

    &:hover {
      box-shadow: 0 2px 8px rgba(99, 102, 241, 0.25);
    }
  }
`;

const StatusText = styled(Typography)`
  animation: ${fadeIn} 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both;
`;

const AuthenticationLayer: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch();

  const { isError, isLoading, canMoveToNextLayer } = useAuthenticationLayer();

  const clearCacheAndRedirect = useCallback(() => {
    dispatch(signOut());
  }, [dispatch]);

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingCard>
          <LogoWrapper>
            <StyledLogo />
          </LogoWrapper>
          <StatusText variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
            Authenticating
          </StatusText>
          <StatusText variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Please wait while we verify your credentials
          </StatusText>
          <ProgressBarContainer>
            <ProgressBar />
          </ProgressBarContainer>
          <DelayRenderer>
            <StyledButton
              onClick={clearCacheAndRedirect}
              variant="outlined"
              color="primary"
              fullWidth
            >
              Cancel
            </StyledButton>
          </DelayRenderer>
        </LoadingCard>
      </LoadingContainer>
    );
  }

  if (isError) {
    return <AuthenticationFailed onReturnToLogin={clearCacheAndRedirect} />;
  }

  /** Auth token and `me` is fetched
   * Move onto security layer for super admin check
   */
  if (canMoveToNextLayer) {
    return <div>{children}</div>;
  }

  return null;
};

export default AuthenticationLayer;

/**
 * Auto-redirects to login instead of waiting for the user to click the button
 * (GLX-1743). The redirect is loop-guarded: if authentication keeps failing,
 * automatic redirects stop and the user gets the manual button only.
 */
const AuthenticationFailed: FC<{ onReturnToLogin: () => void }> = ({ onReturnToLogin }) => {
  // Decide once on mount so the copy doesn't flip mid-render
  const [willAutoRedirect] = useState(() => canAutoRedirectToLogin());

  useEffect(() => {
    if (!willAutoRedirect) return;

    const timer = setTimeout(() => {
      // Record only when the automatic redirect actually fires — a manual
      // click before the timeout should not consume a guard slot
      recordAutoLoginRedirect();
      onReturnToLogin();
    }, AUTH_FAILURE_REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [willAutoRedirect, onReturnToLogin]);

  return (
    <LoadingContainer>
      <ErrorCard>
        <LogoWrapper>
          <StyledLogo />
        </LogoWrapper>
        <StatusText variant="h5" gutterBottom color="error" sx={{ fontWeight: 600 }}>
          Authentication Failed
        </StatusText>
        <StatusText variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          {willAutoRedirect
            ? 'There was an issue authenticating your account. Redirecting you to the login page…'
            : 'There was an issue authenticating your account. Please try logging in again.'}
        </StatusText>
        <StyledButton onClick={onReturnToLogin} variant="contained" color="primary" fullWidth>
          Return to login
        </StyledButton>
      </ErrorCard>
    </LoadingContainer>
  );
};

const DelayRenderer: FC<PropsWithChildren> = ({ children }) => {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldRender(true);
    }, 2000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  if (shouldRender) {
    return <>{children}</>;
  }

  return null;
};
